import React, {CSSProperties, ReactNode, useCallback, useEffect, useState, MouseEvent} from "react";
import {IWishList} from "../../../interfaces";
import AddToExistingList from "./add-to-existing-list";
import {Popup} from "../../../../../ui/popup/popup";
import NewWishlist from "./new-list";
import ItemAddedToList from "./item-added-to-list";
import {addItemToWishlist, onLoadAddToWishlist} from "../../../DAL/data-service";
import FormPreloader from "../../../../../ui/forms/form-preloader";
import {getIsAuthenticated} from "../../../../../domain/hidden-data-provider";
import {getReturnURL} from "../../../../../domain/url-provider";
import {sendGAEvent} from "../../../../../domain/marketing/gtm-provider";

/**
 * Add to wish list component.
 *
 * OnClick:
 * user has no wishlists -> create wishlist (new wishlist's name :username's list) and add the item to it
 * user has one wishlist? add item to it -> return wishlist data
 * user has more than one wishlists -> show all user's wishlist to choose from
 *
 *
 * mandatory prop:
 * skuid (product's skuid)
 *
 *
 * example 1:
 * <AddToWishlist skuid={product.skuid}>
 *   <div className="flex items-center justify-center text-sm mt-1.5 cursor-pointer">
 *      <WishlistIcon width="10px" color="black" /> <span className="pl-1 pt-0.5">Add to Wish List</span>
 *   </div>
 * </AddToWishlist>
 *
 *
 * example 2:
 * <div onClick={()=> setShowPopup(true)}  className={"w-btn cursor-pointer"}>Click To Show Wishlist Popup</div>
 * ................
 * ................
 * <AddToWishlist skuid={product.skuid} isShowPopup={showAddPopup} hidePopup={() => setShowAddPopup(false)} ></AddToWishlist>
 *
 *
 */


interface IAddToWishlistProps {
    width?: string,
    height?: string,
    children?: ReactNode,
    classes?: string,
    styles?: CSSProperties,
    skuid:number,
    isShowPopup?:boolean,
    hidePopup?:()=> any
}

export const AddToWishlist = (props: IAddToWishlistProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showAddToWishlistPopup, setShowAddToWishlistPopup] = useState(props.isShowPopup !== null ? props.isShowPopup : false);
    const [showCreateWishlist, setShowCreateWishlist] = useState(false);
    const [showSelectWishlists, setShowSelectWishlists] = useState(false);
    const [showItemAddedToList, setShowItemAddedToList] = useState(false);
    const [addToWishList, setAddToWishList] = useState<IWishList | null>(null);

    const [wishlists, setWishlists] = useState([] as IWishList[]);

    const hideAddToWishlistPopup =()=>{
        setShowAddToWishlistPopup(false);
        setShowItemAddedToList(false);
        setShowSelectWishlists(false);
        setShowCreateWishlist(false);
        if(props.hidePopup !== null && props.hidePopup !== undefined){
            props.hidePopup();
        }
    }
    const showCreateNewlist =()=>{
        setShowItemAddedToList(false);
        setShowSelectWishlists(false);
        setShowCreateWishlist(true);
    }

    /*
    user has no wishlists? create one and add the item to it -> display link to the new wishlist
    user has one wishlist? add item to it -> display link to the existing wishlist
    user has more than one wishlists -> display SelectWishList, shows all existing wishlist for the user to choose
    */
    const LoadAddToWishlist = useCallback(async (evt?: MouseEvent) => {
        evt?.preventDefault();
        evt?.stopPropagation();

        if(showAddToWishlistPopup){
            //if AddToWishlist popup is already open
            return;
        }

        //user has to be authenticated ->
        let isAuthenticated = getIsAuthenticated();
        if(!isAuthenticated){
            // redirect to login page
            window.location.href = getReturnURL();
            return;
        }

        sendGAEvent("Wish List","Add to Wish List Click");

        setShowAddToWishlistPopup(true);
        setIsLoading(true);
        const response = await onLoadAddToWishlist(props.skuid,0,"");
        if(!response || response.statusCode !== 200 || !response.data.relatedData) return;
        setWishlists(response.data.relatedData);
        if((response.data.relatedData as IWishList[]).length === 0){
            //user has no wishlists -> create wishlist (new wishlist's name :username's list) and add the item to it
            const createListResponse = await addItemToWishlist(props.skuid,0,"");
            if(!createListResponse || createListResponse.statusCode !== 200 || !createListResponse.data.relatedData) return;
            sendGAEvent("Wish List","Create New Wish List");
            let currentWishlist = (createListResponse.data.relatedData as IWishList);
            setAddToWishList(currentWishlist);
            setShowItemAddedToList(true);
            setShowSelectWishlists(false);
            setShowCreateWishlist(false);
        }
        else if((response.data.relatedData as IWishList[]).length === 1){
            //user has one wishlist -> the item added to it
            let currentWishlist = (response.data.relatedData as IWishList[])[0]
            setAddToWishList(currentWishlist);
            setShowItemAddedToList(true);
            setShowSelectWishlists(false);
            setShowCreateWishlist(false);

        }else if((response.data.relatedData as IWishList[]).length > 1){
            //user has more than one wishlist -> show all wishlists for user selection
            setShowSelectWishlists(true);
            setShowItemAddedToList(false);
            setShowItemAddedToList(false);
        }
        setIsLoading(false);
    },[setAddToWishList,setShowItemAddedToList,setShowSelectWishlists,setShowCreateWishlist,showAddToWishlistPopup,props.skuid])

    useEffect(() => {
        if(props.isShowPopup){
            LoadAddToWishlist();
        }
    }, [props.isShowPopup,LoadAddToWishlist]);

    return (
        <div style={{
                ...(props.styles || {}),
                width: props.width,
                height: props.height,
                }}
             className={ `text-gray-800 cursor-default ${ props.classes? props.classes: '' }`}
             onClick={ LoadAddToWishlist }>

            { props.children }

            {(showAddToWishlistPopup) &&
                <Popup
                    canBeClosed={ true }
                    prevent={ false }
                    classes={"p-4"}
                    close={hideAddToWishlistPopup}>
                    {isLoading ?
                        <div className="w-80 flex items-center justify-center"><FormPreloader /></div>
                        :
                        <>
                            {showItemAddedToList && <ItemAddedToList addToWishList={addToWishList}
                                                                     showCreateNewlist={showCreateNewlist}
                                                                     hideAddToWishlistPopup={hideAddToWishlistPopup} />}
                            {showSelectWishlists && <AddToExistingList skuid={props.skuid}
                                                                       showCreateNewlist={showCreateNewlist}
                                                                       wishlists={wishlists}
                                                                       hideAddToWishlistPopup={hideAddToWishlistPopup}/>
                            }
                            {showCreateWishlist && <NewWishlist hideAddToWishlistPopup={hideAddToWishlistPopup}/>}
                        </>
                    }
                </Popup>
            }

        </div>
    )

}